<template>
    <main class="rp-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/company/roles" class="tbv2-crumb">
                    {{ $t('menu.companies.roles') }}
                </router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ $t('roles.permissions_title') }}</span>
            </div>
        </portal>

        <header class="rp-hero">
            <router-link to="/company/roles" class="rp-back">
                <i class="fal fa-arrow-left"></i>
                <span>{{ $t('common.back') }}</span>
            </router-link>
            <h1 class="rp-hero-title">
                {{ $t('roles.permissions_title') }}
                <span v-if="role" class="rp-hero-role">{{ roleLabel }}</span>
            </h1>
            <p class="rp-hero-sub">{{ $t('roles.permissions_subtitle') }}</p>
        </header>

        <section class="rp-card">
            <div v-if="isLoading" class="rp-state">
                <BmvLoader :active="true" />
            </div>

            <div v-else-if="loadError" class="rp-state rp-state--error">
                <i class="fal fa-exclamation-triangle"></i>
                <p>{{ $t('permissions.load_error') }}</p>
                <BmvButton variant="primary" size="sm" @click="load">
                    {{ $t('permissions.retry') }}
                </BmvButton>
            </div>

            <div v-else-if="!role" class="rp-state rp-state--error">
                <i class="fal fa-user-slash"></i>
                <p>{{ $t('roles.not_found') }}</p>
                <BmvButton variant="primary" size="sm" @click="goBack">
                    {{ $t('roles.back_to_list') }}
                </BmvButton>
            </div>

            <RolePermissionsEditor
                v-else
                :role="role"
                :groups="permissionGroups"
                :disabled="isLocked"
                @save="handleSave"
            />
        </section>
    </main>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import BmvLoader from '@/components/UI/v2/BmvLoader.vue'
import RolePermissionsEditor from '@/components/Model/Company/RolePermissionsEditor.vue'
import { getRoleSlug, roleDisplayName } from '@/helpers/roles'

export default {
    name: 'CompanyRolePermissions',
    components: { BmvButton, BmvLoader, RolePermissionsEditor },
    data() {
        return {
            isLoading: true,
            loadError: false
        }
    },
    computed: {
        ...mapState('role', {
            roles: (state) => state.roles,
            permissionGroups: (state) => state.permissionGroups
        }),
        ...mapState('auth', {
            currentRole: (state) => state.role
        }),
        roleId() {
            return this.$route.params.roleId
        },
        role() {
            return (
                this.roles.find((role) => String(role.id) === String(this.roleId)) || null
            )
        },
        roleLabel() {
            return roleDisplayName(this.$i18n, this.role)
        },
        /**
         * Seul garde-fou côté client : `super-admin` n'est pas modifiable
         * depuis le back-office d'une commune. Les rôles issus d'un modèle
         * plateforme (`is_system`) restent, eux, pleinement modifiables.
         */
        isLocked() {
            if (!this.role) return true
            if (getRoleSlug(this.role) !== 'super-admin') return false
            return getRoleSlug(this.currentRole) !== 'super-admin'
        }
    },
    created() {
        this.load()
    },
    methods: {
        ...mapActions('role', ['getRoles', 'getPermissionCatalogue', 'updateRole']),
        async load() {
            this.isLoading = true
            this.loadError = false
            try {
                // Le catalogue retombe seul sur sa version locale en cas d'échec :
                // seule la liste des rôles est bloquante.
                await Promise.all([
                    this.getRoles(),
                    this.getPermissionCatalogue().catch(() => {})
                ])
            } catch (error) {
                this.loadError = true
                this.$store.dispatch('dispatchError', error)
            } finally {
                this.isLoading = false
            }
        },
        goBack() {
            this.$router.push('/company/roles')
        },
        handleSave({ permissions, callback }) {
            this.updateRole({ id: this.role.id, permissions })
                .then(() => {
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('permissions.save_success'),
                        message: ''
                    })
                    callback(true)
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                    callback(false)
                })
        }
    }
}
</script>

<style scoped>
.rp-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tbv2-crumb {
    color: var(--v2-text-3);
    text-decoration: none !important;
    font-weight: var(--v2-font-weight-medium);
    transition: color var(--v2-transition-fast);
}

.tbv2-crumb:hover {
    color: var(--v2-primary);
}

.tbv2-sep {
    color: var(--v2-text-4);
}

.tbv2-current {
    color: var(--v2-text-1);
}

.rp-hero {
    margin-bottom: var(--v2-space-5);
}

.rp-back {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    transition: color var(--v2-transition-fast);
}

.rp-back:hover {
    color: var(--v2-primary);
}

.rp-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.rp-hero-role {
    display: inline-flex;
    align-items: center;
    padding: 4px 14px;
    border-radius: 999px;
    background: #0891b2;
    color: #fff;
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    letter-spacing: 0;
}

.rp-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 820px;
}

.rp-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
    position: relative;
    min-height: 240px;
}

.rp-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-6) var(--v2-space-5);
    text-align: center;
    color: var(--v2-text-3);
}

.rp-state--error i {
    font-size: 1.75rem;
    color: var(--v2-warning);
}

.rp-state p {
    margin: 0;
    font-size: var(--v2-text-sm-size);
}

@media (max-width: 900px) {
    .rp-v2 {
        padding: 16px 18px 32px;
    }

    .rp-hero-title {
        font-size: 1.375rem;
    }
}
</style>
